import { useMemo, useRef } from 'react'
import type { FocusEvent } from 'react'
import type { TimeRow } from '@/types/schedule'

type Props = {
  weeklySchedules: TimeRow[]
  weekStartDate: string
  selectedScheduleId: string | null
  onSelectSchedule: (scheduleId: string) => void
  onWeekChange: (direction: 'prev' | 'next') => void
  isLoading?: boolean
  // 見出しを追従させる位置（画面上端に固定表示される要素がある場合はその高さ）
  stickyTop?: number
}

const DAYS_OF_WEEK = ['日', '月', '火', '水', '木', '金', '土']

// 週送り行の高さ（px）。日付行はこの分だけ下で追従させる。
// 文字サイズを拡大しても日付行と重ならないよう、行もボタンも rem ではなくこの px 値で高さを決める
const WEEK_NAV_HEIGHT = 40

// 日付文字列をローカルタイムゾーンでパース（UTC解釈によるズレを防止）
function parseLocalDate(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number)
  return new Date(year, month - 1, day)
}

// 今日の日付をローカルタイムゾーンで取得（時刻を0:00にリセット）
function getToday(): Date {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

export default function WeeklyCalendar({
  weeklySchedules,
  weekStartDate,
  selectedScheduleId,
  onSelectSchedule,
  onWeekChange,
  isLoading = false,
  stickyTop = 0,
}: Props) {
  // 週の日付を計算
  const weekDates = useMemo(() => {
    const startDate = parseLocalDate(weekStartDate)
    const todayTime = getToday().getTime()
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + i)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return {
        date: `${year}-${month}-${day}`,
        day: date.getDate(),
        month: date.getMonth() + 1,
        dayOfWeek: DAYS_OF_WEEK[date.getDay()],
        isToday: todayTime === date.getTime(),
        isSunday: date.getDay() === 0,
        isSaturday: date.getDay() === 6,
      }
    })
  }, [weekStartDate])

  // 前の週に戻れるかチェック（今日より前には戻れない）
  const canGoPrev = useMemo(() => {
    const startDate = parseLocalDate(weekStartDate)
    const today = getToday()
    return startDate > today
  }, [weekStartDate])

  const dateRowTop = stickyTop + WEEK_NAV_HEIGHT

  // キーボード操作で枠へフォーカスを移したとき、追従している見出しの裏に隠れていたら見える位置までスクロールする
  // （html に scroll-padding-top を置く方法は、追従中の週送りボタンをタップした際にページが大きく巻き戻るため使わない）
  const theadRef = useRef<HTMLTableSectionElement>(null)
  const revealFocusedSlot = (event: FocusEvent<HTMLButtonElement>) => {
    const headerBottom = dateRowTop + (theadRef.current?.offsetHeight ?? 0)
    const top = event.currentTarget.getBoundingClientRect().top
    if (top < headerBottom) window.scrollBy(0, top - headerBottom)
  }

  // sticky の th は border-collapse の罫線が追従しないため、下罫線は box-shadow で描く（色は本体行と同じ gray-100）
  const dateCellClass = 'sticky bg-white shadow-[0_1px_0_#f3f4f6]'

  return (
    <div className="bg-white rounded-lg">
      {/* 週送り（表の外に置いて、列見出しとして読み上げられないようにする。追従範囲はこの親 div = 表の末尾まで） */}
      <div className="sticky bg-white flex items-center justify-between" style={{ top: stickyTop, height: WEEK_NAV_HEIGHT }}>
        <button
          type="button"
          onClick={() => onWeekChange('prev')}
          disabled={!canGoPrev || isLoading}
          aria-label="前の週"
          style={{ width: WEEK_NAV_HEIGHT, height: WEEK_NAV_HEIGHT }}
          className={`text-2xl leading-none ${
            canGoPrev && !isLoading ? 'text-cyan-600' : 'text-gray-300 cursor-not-allowed'
          }`}
        >
          &#x2039;
        </button>
        <span className="text-sm text-gray-700" aria-live="polite">
          {isLoading ? '読み込み中...' : `${weekDates[0].month}/${weekDates[0].day}〜${weekDates[6].month}/${weekDates[6].day}`}
        </span>
        <button
          type="button"
          onClick={() => onWeekChange('next')}
          disabled={isLoading}
          aria-label="次の週"
          style={{ width: WEEK_NAV_HEIGHT, height: WEEK_NAV_HEIGHT }}
          className={`text-2xl leading-none ${
            !isLoading ? 'text-cyan-600' : 'text-gray-300 cursor-not-allowed'
          }`}
        >
          &#x203A;
        </button>
      </div>

      {/* カレンダーグリッド（日付行はスクロール時に上端へ追従させるため、横スクロールさせずに幅内へ収める） */}
      <table className="w-full border-collapse table-fixed">
        {/* 時刻列の幅。table-fixed では1行目の th の幅指定も効くが、意図を明示するため colgroup で指定する */}
        <colgroup>
          <col className="w-12" />
        </colgroup>
        <thead ref={theadRef}>
          <tr>
            <th scope="col" className={dateCellClass} style={{ top: dateRowTop }}></th>
            {weekDates.map((date) => (
              <th
                key={date.date}
                scope="col"
                style={{ top: dateRowTop }}
                className={`${dateCellClass} px-0 py-2 text-center ${
                  date.isSunday
                    ? 'text-red-500'
                    : date.isSaturday
                      ? 'text-blue-500'
                      : 'text-gray-700'
                }`}
              >
                {/* 幅320px程度の端末では日付が列幅を超えるため一段小さくする */}
                <div className="text-xs xs:text-sm font-normal">
                  {date.month}/{date.day}
                </div>
                {/* 今日の印の有無で見出しの高さが変わらないよう、曜日は常に同じ大きさの箱に入れる */}
                <div
                  className={`text-xs w-6 h-6 flex items-center justify-center mx-auto ${date.isToday ? 'bg-cyan-500 text-white rounded-full' : ''}`}
                >
                  {date.dayOfWeek}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        {/* 読み込み中は直前の週の行をそのまま薄く表示する。表の高さと行の位置が変わらないので、スクロール位置も見ている時間帯もずれない */}
        <tbody className={isLoading ? 'opacity-40 pointer-events-none' : ''} aria-busy={isLoading}>
          {weeklySchedules.length === 0 ? (
            <tr>
              <td colSpan={8} className="text-center py-8 text-gray-500">
                予約可能な枠がありません
              </td>
            </tr>
          ) : (
            weeklySchedules.map((row) => (
              <tr key={row.time} className="border-t border-gray-100">
                <td className="px-1 py-2 text-sm text-gray-500 whitespace-nowrap">
                  {row.time}
                </td>
                {row.slots.map((slot, index) => (
                  <td key={`${row.time}-${index}`} className="px-0 py-1 text-center">
                    {slot.available ? (
                      <button
                        type="button"
                        onClick={() => slot.doctor_schedule_id && onSelectSchedule(slot.doctor_schedule_id)}
                        disabled={isLoading}
                        onFocus={revealFocusedSlot}
                        aria-label={`${weekDates[index].month}/${weekDates[index].day} ${row.time}`}
                        aria-pressed={selectedScheduleId === slot.doctor_schedule_id}
                        className={`w-8 h-8 rounded-full border-2 flex items-center justify-center mx-auto transition-all ${
                          selectedScheduleId === slot.doctor_schedule_id
                            ? 'bg-cyan-500 border-cyan-500 text-white'
                            : 'border-cyan-400 text-cyan-500 hover:bg-cyan-50'
                        }`}
                      >
                        {selectedScheduleId === slot.doctor_schedule_id ? (
                          <span className="text-sm font-bold">&#x25CE;</span>
                        ) : (
                          <span className="text-sm">&#x25CB;</span>
                        )}
                      </button>
                    ) : (
                      // ○ボタンと同じ大きさにして行の高さを揃える（揃っていないと週を切り替えたとき同じ時刻の行の位置がずれる）
                      <span className="w-8 h-8 flex items-center justify-center mx-auto text-gray-300 text-sm">&times;</span>
                    )}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}
